import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type BottomSheetState = {
  content: ReactNode;
} | null;

type BottomSheetContextValue = {
  isOpen: boolean;
  content: ReactNode | null;
  open: (content: ReactNode) => void;
  close: () => void;
};

const BottomSheetContext = createContext<BottomSheetContextValue | null>(null);

type BottomSheetProviderProps = {
  children: ReactNode;
};

export const BottomSheetProvider = ({ children }: BottomSheetProviderProps) => {
  const [sheetState, setSheetState] = useState<BottomSheetState>(null);

  const open = useCallback((content: ReactNode) => {
    setSheetState({ content });
  }, []);

  const close = useCallback(() => {
    setSheetState(null);
  }, []);

  const value: BottomSheetContextValue = {
    isOpen: sheetState !== null,
    content: sheetState?.content ?? null,
    open,
    close,
  };

  return <BottomSheetContext.Provider value={value}>{children}</BottomSheetContext.Provider>;
};

export const useBottomSheet = (): BottomSheetContextValue => {
  const context = useContext(BottomSheetContext);

  if (!context) {
    throw new Error('useBottomSheet must be used within BottomSheetProvider');
  }

  return context;
};
